import { AnnotationMode, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf.mjs"
import { EventBus, LinkTarget, PDFLinkService, PDFViewer, RenderingStates } from "pdfjs-dist/legacy/web/pdf_viewer.mjs"
import { loadPdfDocument } from "../pdf_document_loader.js"
import { rangeForPdfTextAnchor } from "../pdf_text_anchor.js"
import { normalizeSearchNode, normalizeSearchText } from "../search_text.js"
import {
  DEFAULT_HIGHLIGHT_COLOR,
  highlightColor,
  readHighlightColor,
  selectColorButtons,
  validHighlightColor
} from "../highlight_colors.js"
import {
  closeNoteDialog,
  copyTextToClipboard,
  openNoteDialog,
  removeBookmarkRecords,
  removeHighlightRecord,
  renderBookmarkList,
  renderHighlightList,
  shareAnnotationLocation,
  saveHighlightNote,
  updateHighlightColor
} from "../reader_annotations.js"
import { requestJson } from "../reader_api.js"
import { ReaderSearch } from "../reader_search.js"
import { ReaderSidebar } from "../reader_sidebar.js"
import { startReaderErrorTracking } from "../reader_error_tracking.js"
import { readerFindShortcut, readerPageShortcut } from "../reader_keyboard_shortcut.js"
import { consumeReaderLocation } from "../reader_location.js"
import PdfOfflineSession from "../pdf_offline_session.js"
import PdfPageOverview from "../pdf_page_overview.js"
import ReaderController from "./reader_controller.js"

const SEARCH_RETRY_INTERVAL = 50
const SEARCH_SETTLE_DURATION = 200

export default class extends ReaderController {
  static targets = [
    "viewport", "viewer", "sidebar", "toc", "highlights", "bookmarks", "chapter",
    "selectionDialog", "selectionQuote", "selectionStatus", "translationButton", "translationAvailability",
    "translationDialog", "translationOriginal", "translationLanguage", "translationStatus", "translationResult",
    "translationText", "translationCopyStatus", "translationSubmit", "noteDialog", "noteInput", "noteTitle",
    "panel", "panelButton", "searchInput", "searchStatus", "searchResults",
    "slider", "status", "sidebarToggle", "bookmarkButton", "selectionColor", "noteColor", "colorStatus", "highlightFilter", "highlightStatus", "scaleValue",
    "offlineRestricted", "offlineIndicator", "debugError", "debugStatus", "signInLink",
    "pageOverviewButton", "pageOverviewDialog", "pageOverviewGrid"
  ]

  static values = {
    documentUrl: String,
    workerUrl: String,
    progressUrl: String,
    highlightsUrl: String,
    bookmarksUrl: String,
    translationUrl: String,
    searchResultsUrl: String,
    preferencesUrl: String,
    readOnly: { type: Boolean, default: false },
    startLocation: { type: Object, default: {} },
    sharedLocation: { type: Object, default: {} },
    pageCount: Number,
    theme: { type: String, default: "light" },
    zoom: { type: Number, default: 100 },
    initialSearch: { type: Object, default: {} },
    labels: Object,
    highlightColors: Object,
    offlineManifestUrl: String,
    userId: Number,
    bookId: Number,
    editionId: Number,
    appsignalApiKey: String
  }

  async connect() {
    const connection = Symbol("pdf-reader-connection")
    this.connection = connection
    if (!this.browserSupportsPdfReader()) {
      const missingFeatures = this.missingPdfReaderFeatures().join(", ")
      const unsupportedBrowserError = new Error(`Unsupported PDF reader browser. Missing APIs: ${missingFeatures}`)
      this.renderReaderError(
        this.labelsValue.unsupportedBrowser,
        unsupportedBrowserError
      )
      return
    }
    this.errorTracker = this.startErrorTracking()

    this.highlightRecords = []
    this.highlightFilterColor = ""
    this.pendingHighlightColor = this.readOnlyValue ? DEFAULT_HIGHLIGHT_COLOR : readHighlightColor(this.highlightColorsValue)
    this.bookmarkRecords = []
    this.bookmarksLoaded = false
    this.bookmarkTogglePending = false
    this.pendingSelection = null
    this.pendingHighlightLocations = new Set()
    this.loadedPanels = new Set()
    this.loadingPanels = new Map()
    this.progressTimer = null
    this.highlightStatusTimer = null
    this.currentSearchOverlay = null
    this.currentSearchResult = null
    this.currentSearchResultKey = null
    this.abortController = new AbortController()
    this.offlineSession = PdfOfflineSession.forReader({
      readOnly: this.readOnlyValue,
      userId: this.userIdValue,
      bookId: this.bookIdValue,
      editionId: this.editionIdValue,
      manifestUrl: this.offlineManifestUrlValue,
      modeChanged: (offline) => this.setOfflineMode(offline),
      storageUnavailable: () => this.renderOfflineStorageUnavailable(),
      loadOnlinePanels: () => Promise.all([this.loadPanel("highlights"), this.loadPanel("bookmarks")]),
      invalidateReader: () => this.invalidateOfflineReader(),
      closeReader: () => this.closePdfDocument()
    })
    GlobalWorkerOptions.workerSrc = this.workerUrlValue
    this.applyChromeTheme()
    this.readerSidebar = new ReaderSidebar({
      element: this.element,
      sidebar: this.sidebarTarget,
      toggle: this.sidebarToggleTarget,
      panels: this.panelTargets,
      buttons: this.panelButtonTargets,
      onSearchVisibilityChanged: (visible) => visible ? this.renderSearchResultHighlights() : this.removeSearchResultHighlights(),
      onDesktopLayout: () => new Promise((resolve) => window.requestAnimationFrame(() => {
        this.resize()
        resolve()
      }))
    })
    this.readerSearch = new ReaderSearch({
      url: this.searchResultsUrlValue,
      input: this.searchInputTarget,
      status: this.searchStatusTarget,
      results: this.searchResultsTarget,
      labels: this.labelsValue,
      request: requestJson,
      titleFor: (result) => result.section_title || `${this.labelsValue.page} ${result.source.page}`,
      openResult: (result, options) => this.openSearchResult(result, options),
      resetHighlights: () => this.resetSearchHighlights(),
      renderHighlights: () => this.renderSearchResultHighlights()
    })

    // Include offline setup and metadata HEAD in the readiness contract: the
    // loading task does not exist yet when the sidebar first becomes usable.
    this.documentReady = this.initializeDocument(connection)
    try {
      await this.documentReady
    } catch (error) {
      if (this.connection !== connection) return
      this.renderReaderError(this.labelsValue.loadError, error)
      this.errorTracker.reportError(error)
      console.error("Unable to initialize PDF reader", error)
    }
  }

  async initializeDocument(connection) {
    await this.offlineSession?.connect()
    if (this.connection !== connection || this.offlineSession?.legalAcceptanceBlocked) return false

    this.eventBus = new EventBus()
    this.linkService = new PDFLinkService({
      eventBus: this.eventBus,
      externalLinkTarget: LinkTarget.BLANK,
      externalLinkRel: "noopener noreferrer"
    })
    this.pdfViewer = new PDFViewer({
      container: this.viewportTarget,
      viewer: this.viewerTarget,
      eventBus: this.eventBus,
      linkService: this.linkService,
      annotationMode: AnnotationMode.ENABLE,
      annotationEditorMode: -1,
      enableScripting: false,
      enableAutoLinking: false,
      supportsPinchToZoom: true,
      abortSignal: this.abortController.signal
    })
    this.linkService.setViewer(this.pdfViewer)
    this.registerViewerEvents()

    this.loadingTask = await loadPdfDocument({
      url: this.documentUrlValue,
      signal: this.abortController.signal,
      offlineRecord: this.offlineSession?.record,
      store: this.offlineSession?.store,
      options: {
        enableScripting: false,
        isEvalSupported: false
      }
    })
    this.pdfDocument = await this.loadingTask.promise
    this.linkService.setDocument(this.pdfDocument)
    this.pdfViewer.setDocument(this.pdfDocument)
    return true
  }

  startErrorTracking() {
    return startReaderErrorTracking({
      key: this.appsignalApiKeyValue,
      action: "Reader#pdf"
    })
  }

  disconnect() {
    this.connection = null
    window.clearTimeout(this.progressTimer)
    window.clearTimeout(this.highlightStatusTimer)
    window.clearTimeout(this.resizeTimer)
    this.readerSearch?.disconnect()
    this.pageOverview?.disconnect()
    this.errorTracker?.disconnect()
    this.abortController?.abort()
    this.loadingTask?.destroy()
    this.offlineSession?.disconnect()
  }

  // Keep browser diagnostics and system assertions observable without moving
  // offline record ownership back out of PdfOfflineSession.
  get offlineRecord() {
    return this.offlineSession?.record
  }

  browserSupportsPdfReader() {
    return this.missingPdfReaderFeatures().length === 0
  }

  missingPdfReaderFeatures() {
    // Keep this list in sync when upgrading pdfjs-dist so any capabilities not
    // supplied by its legacy build remain user-facing compatibility errors.
    return [
      ["AbortSignal.any", typeof AbortSignal !== "undefined" && typeof AbortSignal.any === "function"],
      ["structuredClone", typeof structuredClone === "function"]
    ].filter(([, supported]) => !supported).map(([name]) => name)
  }

  renderReaderError(message, error) {
    const errorMessage = `<p class="reader-error">${this.escape(message)}</p>`
    if (!this.debugReaderEnabled() || !error) {
      this.viewportTarget.innerHTML = errorMessage
      return
    }

    const details = `${error.name || "Error"}: ${error.message || String(error)}`
    const diagnostic = error.stack?.includes(details) ? error.stack : [details, error.stack].filter(Boolean).join("\n")
    this.viewportTarget.innerHTML = `${errorMessage}
      <section class="reader-debug" aria-label="${this.escape(this.labelsValue.debugErrorTitle)}">
        <h2>${this.escape(this.labelsValue.debugErrorTitle)}</h2>
        <pre tabindex="0" data-pdf-reader-target="debugError">${this.escape(diagnostic)}</pre>
        <div class="reader-debug__actions">
          <button type="button" class="button button--secondary button--small" data-action="pdf-reader#copyDebugError">
            ${this.escape(this.labelsValue.copyDebugError)}
          </button>
          <span role="status" aria-live="polite" data-pdf-reader-target="debugStatus"></span>
        </div>
      </section>`
  }

  debugReaderEnabled() {
    return typeof URLSearchParams === "function" &&
      new URLSearchParams(globalThis.location?.search || "").get("debug") === "true"
  }

  async copyDebugError() {
    try {
      await this.writeDebugErrorToClipboard(this.debugErrorTarget.textContent)
      this.debugStatusTarget.textContent = this.labelsValue.debugErrorCopied
    } catch (_error) {
      this.debugStatusTarget.textContent = this.labelsValue.debugErrorCopyFailed
      this.debugErrorTarget.focus()
    }
  }

  async writeDebugErrorToClipboard(text) {
    if (!await copyTextToClipboard(text)) throw new Error("Unable to copy reader diagnostic")
  }

  registerViewerEvents() {
    this.eventBus.on("pagesinit", () => this.pagesInitialized())
    this.eventBus.on("updateviewarea", ({ location }) => this.positionChanged(location))
    this.eventBus.on("pagerendered", ({ pageNumber }) => {
      this.renderHighlightsForPage(pageNumber)
    })
    this.eventBus.on("textlayerrendered", ({ pageNumber }) => {
      this.renderCurrentSearchHighlightForPage(pageNumber)
      this.renderSearchResultHighlightsForPage(pageNumber)
    })
  }

  async pagesInitialized() {
    this.initializePageOverview()

    const position = this.sharedPosition() || this.savedPosition()
    this.pdfViewer.currentPageNumber = position.page
    consumeReaderLocation()
    await this.restoreInitialPosition(position)
    if (this.readOnlyValue) {
      await this.openInitialSearch()
    } else if (this.offlineMode) {
      await this.loadPanel("toc")
    } else {
      await Promise.all([this.loadPanel("highlights"), this.loadPanel("bookmarks")])
      await this.openInitialSearch()
    }
  }

  async restoreInitialPosition(position) {
    let navigation = this.navigation
    // Initialization restores the saved offset itself; zoom must not create a
    // second navigation that hides a newer user action from this guard.
    await this.applyRelativeZoom({ restorePosition: false })
    if (this.navigation !== navigation) return

    const scrolling = this.scrollToPosition(position.page, position.y)
    navigation = this.navigation
    await scrolling
    if (this.navigation !== navigation) return

    await this.waitForPageRendered(position.page)
    if (this.navigation !== navigation) return

    await this.openSharedSemanticLocation()
  }

  initializePageOverview() {
    if (this.pageOverview) return

    this.pageOverview = new PdfPageOverview({
      dialog: this.pageOverviewDialogTarget,
      grid: this.pageOverviewGridTarget,
      pdfDocument: this.pdfDocument,
      pageLabel: this.labelsValue.pageNumber,
      onSelect: (pageNumber) => this.scrollToPosition(pageNumber, 0),
      onError: (error) => {
        console.error("Unable to render PDF page thumbnail", error)
        this.errorTracker.reportError(error)
      }
    })
    this.pageOverviewButtonTarget.disabled = false
  }

  waitForPageRendered(pageNumber) {
    const pageView = this.pdfViewer.getPageView(pageNumber - 1)
    if (pageView?.renderingState === RenderingStates.FINISHED) return Promise.resolve()

    return new Promise((resolve) => {
      const rendered = ({ pageNumber: renderedPage }) => {
        if (renderedPage !== pageNumber) return

        this.eventBus.off("pagerendered", rendered)
        resolve()
      }
      this.eventBus.on("pagerendered", rendered)
    })
  }

  savedPosition() {
    const local = this.offlineSession?.record?.progress?.location
    const saved = local?.format === "pdf" ? local : this.startLocationValue
    const page = Math.max(1, Math.min(this.pdfDocument.numPages, Number(saved.page) || 1))
    const y = Math.max(0, Math.min(1, Number(saved.y) || 0))
    return { page, y }
  }

  sharedPosition() {
    const shared = this.sharedLocationValue
    if (shared.location) {
      const page = Math.max(1, Math.min(this.pdfDocument.numPages, Number(shared.location.page) || 1))
      const y = Math.max(0, Math.min(1, Number(shared.location.y) || 0))
      return { page, y }
    }
    if (shared.semantic_locator?.source?.page) {
      const page = Math.max(1, Math.min(this.pdfDocument.numPages, Number(shared.semantic_locator.source.page)))
      return { page, y: 0 }
    }
    return null
  }

  async openSharedSemanticLocation() {
    const locator = this.sharedLocationValue.semantic_locator
    if (!locator) return

    try {
      const page = this.pageElement(locator.source.page)
      const textLayer = page?.querySelector(".textLayer")
      const range = textLayer && rangeForPdfTextAnchor(textLayer, {
        preferredOffset: locator.start_offset,
        quote: locator.quote,
        prefix: locator.prefix,
        suffix: locator.suffix
      })
      const y = range && this.normalizedRects(range, page)[0]?.y
      if (!Number.isFinite(y)) throw new Error("Shared PDF range is unavailable")

      await this.scrollToPosition(locator.source.page, y)
    } catch (error) {
      console.error("Unable to open shared PDF location", error)
      const savedPosition = this.savedPosition()
      this.pdfViewer.currentPageNumber = savedPosition.page
      await this.scrollToPosition(savedPosition.page, savedPosition.y)
      this.showHighlightStatus(this.labelsValue.sharedLocationError)
    }
  }

  previous() {
    this.scrollToPosition(Math.max(1, this.currentPosition().page - 1), 0)
  }

  next() {
    this.scrollToPosition(Math.min(this.pdfDocument.numPages, this.currentPosition().page + 1), 0)
  }

  openPageOverview() {
    this.pageOverview?.open({
      currentPage: this.currentPosition().page,
      opener: this.pageOverviewButtonTarget
    })
  }

  closePageOverview() {
    this.pageOverview?.close()
  }

  scrub() {
    const percentage = Number(this.sliderTarget.value)
    if (percentage >= 100) {
      this.scrollToPosition(this.pdfDocument.numPages, 1)
      return
    }

    const raw = percentage / 100 * this.pdfDocument.numPages
    const page = Math.min(this.pdfDocument.numPages, Math.floor(raw) + 1)
    this.scrollToPosition(page, Math.max(0, Math.min(1, raw - Math.floor(raw))))
  }

  async increaseZoom() {
    this.zoomValue = Math.min(200, this.zoomValue + 10)
    await this.applyRelativeZoom()
    await this.savePreferences()
  }

  async decreaseZoom() {
    this.zoomValue = Math.max(50, this.zoomValue - 10)
    await this.applyRelativeZoom()
    await this.savePreferences()
  }

  zoomValueChanged(value) {
    this.scaleValueTarget.textContent = `${value}%`
  }

  async applyRelativeZoom({ restorePosition = true } = {}) {
    if (!this.pdfViewer) return

    const zoomOperation = this.zoomOperation = Symbol("pdf-zoom")
    const navigation = this.navigation
    const position = this.currentPosition()
    this.pdfViewer.currentScaleValue = "page-width"
    await this.nextFrame()
    if (this.zoomOperation !== zoomOperation) return

    const fitWidth = this.pdfViewer.currentScale
    this.pdfViewer.currentScale = fitWidth * this.zoomValue / 100
    await this.nextFrame()
    // A delayed resize must not restore the old page after a newer navigation.
    if (this.zoomOperation !== zoomOperation || this.navigation !== navigation) return

    if (restorePosition) await this.scrollToPosition(position.page, position.y)
  }

  resize() {
    window.clearTimeout(this.resizeTimer)
    this.resizeTimer = window.setTimeout(() => this.applyRelativeZoom(), 100)
  }

  async toggleTheme() {
    this.themeValue = this.themeValue === "light" ? "dark" : "light"
    this.applyChromeTheme()
    await this.savePreferences()
  }

  applyChromeTheme() {
    this.element.dataset.readerTheme = this.themeValue
  }

  toggleSidebar() {
    const opening = !this.sidebarTarget.classList.contains("is-open")
    this.readerSidebar.setOpen(opening)
    if (opening) this.loadPanel(this.activePanel())
  }

  async openSearchPanel() {
    if (this.offlineMode) return

    this.readerSidebar.activate("search")
    await this.readerSidebar.setOpen(true)
    this.searchInputTarget.focus()
  }

  setSidebarOpen(open) {
    return this.readerSidebar.setOpen(open)
  }

  showPanel(event) {
    const selected = event.currentTarget.dataset.panelName
    if (this.readOnlyValue && ["highlights", "bookmarks"].includes(selected)) return
    if (this.offlineMode && selected !== "toc") return
    this.activatePanel(selected)
    this.loadPanel(selected)
  }

  activatePanel(selected) {
    this.readerSidebar.activate(selected)
  }

  activePanel() {
    return this.readerSidebar.activePanel()
  }

  loadPanel(selected) {
    if (this.readOnlyValue && ["highlights", "bookmarks"].includes(selected)) return Promise.resolve()
    if (this.offlineMode && selected !== "toc") return Promise.resolve()
    if (!selected || this.loadedPanels.has(selected)) return Promise.resolve()
    if (this.loadingPanels.has(selected)) return this.loadingPanels.get(selected)

    const loader = this.panelLoader(selected)
    if (!loader) return Promise.resolve()

    const loading = (this.documentReady || this.loadingTask?.promise || Promise.resolve())
      .then((ready) => ready === false ? false : loader())
      .then((loaded) => {
        if (loaded !== false) this.loadedPanels.add(selected)
      })
      .catch((error) => console.error(`Unable to load PDF ${selected} panel`, error))
      .finally(() => this.loadingPanels.delete(selected))
    this.loadingPanels.set(selected, loading)
    return loading
  }

  panelLoader(selected) {
    if (selected === "toc") return () => this.loadOutline()
    if (selected === "highlights") return () => this.loadHighlights()
    if (selected === "bookmarks") return () => this.loadBookmarks()
  }

  positionChanged() {
    const position = this.currentPosition()
    this.updateSignInLocation(position)
    this.syncBookmarkButton(position)
    const percentage = this.percentage(position)
    this.chapterTarget.textContent = this.labelsValue.pageOf
      .replace("%{page}", position.page)
      .replace("%{pages}", this.pdfDocument.numPages)
    this.sliderTarget.value = percentage

    if (!this.readOnlyValue) {
      window.clearTimeout(this.progressTimer)
      this.progressTimer = window.setTimeout(() => this.saveProgress(position, percentage), 700)
    }
  }

  updateSignInLocation(position) {
    if (!this.readOnlyValue || !this.hasSignInLinkTarget) return

    const url = new URL(this.signInLinkTarget.href)
    url.searchParams.set("location[format]", "pdf")
    url.searchParams.set("location[page]", position.page)
    url.searchParams.set("location[y]", position.y)
    this.signInLinkTarget.href = url.toString()
  }

  currentPosition() {
    if (!this.pdfDocument) return { page: 1, y: 0 }

    const visible = this.pdfViewer?._getVisiblePages?.().views?.[0]
    const page = visible?.id || this.pdfViewer.currentPageNumber || 1
    const pageElement = this.pageElement(page)
    if (!pageElement) return { page, y: 0 }

    const viewportRect = this.viewportTarget.getBoundingClientRect()
    const pageRect = pageElement.getBoundingClientRect()
    const atDocumentEnd = page === this.pdfDocument.numPages &&
      Math.ceil(this.viewportTarget.scrollTop + this.viewportTarget.clientHeight) >= this.viewportTarget.scrollHeight
    if (atDocumentEnd) return { page, y: 1 }

    const y = Math.max(0, Math.min(1, (viewportRect.top - pageRect.top) / pageRect.height))
    return { page, y }
  }

  percentage({ page, y }) {
    return Math.round(((page - 1 + y) / this.pdfDocument.numPages) * 10_000) / 100
  }

  async scrollToPosition(page, y) {
    if (!this.pdfViewer || !this.pdfDocument) return

    const navigation = this.navigation = Symbol("pdf-navigation")
    this.updateSignInLocation({ page, y })
    this.pdfViewer.scrollPageIntoView({ pageNumber: page })
    await this.nextFrame()
    if (this.navigation !== navigation) return

    const pageElement = this.pageElement(page)
    if (pageElement) {
      this.viewportTarget.scrollTop += pageElement.getBoundingClientRect().height * y
      this.positionChanged()
    }
  }

  async saveProgress(position, percentage) {
    if (this.readOnlyValue) return
    if (this.offlineSession?.superseded) return

    const semanticLocator = await this.semanticLocatorForPosition(position.page)
    const progress = {
      location: { format: "pdf", page: position.page, y: position.y },
      semantic_locator: semanticLocator,
      current_section: this.chapterTarget.textContent,
      progress_percentage: percentage
    }

    await this.offlineSession.saveProgress(
      progress,
      () => requestJson(this.progressUrlValue, { method: "PATCH", body: { reading: progress } })
    )
  }

  async toggleBookmark() {
    if (this.readOnlyValue) return
    if (this.offlineMode) return
    if (!this.pdfDocument || !this.bookmarksLoaded || this.bookmarkTogglePending) return

    this.bookmarkTogglePending = true
    this.bookmarkButtonTarget.disabled = true
    try {
      const bookmarks = this.visibleBookmarks()
      if (bookmarks.length > 0) {
        await this.removeBookmarks(bookmarks)
      } else {
        await this.addBookmark()
      }
    } finally {
      this.bookmarkTogglePending = false
      this.bookmarkButtonTarget.disabled = !this.bookmarksLoaded
      this.syncBookmarkButton()
    }
  }

  async addBookmark() {
    if (this.offlineMode) return

    const position = this.currentPosition()
    const semanticLocator = await this.semanticLocatorForPosition(position.page)

    const record = await requestJson(this.bookmarksUrlValue, {
      method: "POST",
      body: {
        bookmark: {
          location: { format: "pdf", page: position.page, y: position.y },
          semantic_locator: semanticLocator,
          current_section: this.chapterTarget.textContent,
          label: this.chapterTarget.textContent,
          progress_percentage: this.percentage(position)
        }
      }
    })
    if (!record) return

    this.bookmarkRecords.push(record)
    this.renderBookmarks()
    this.syncBookmarkButton()
  }

  async semanticLocatorForPosition(pageNumber) {
    try {
      const page = await this.pdfDocument.getPage(pageNumber)
      const text = (await page.getTextContent()).items.map((item) => item.str).join(" ")
      const quote = text.trim().slice(0, 200)
      if (!quote) return null

      const length = normalizeSearchText(quote).length
      return {
        format: "pdf",
        source: { page: pageNumber },
        block_index: 0,
        start_offset: 0,
        end_offset: length,
        quote,
        prefix: "",
        suffix: normalizeSearchText(text).slice(length, length + 80)
      }
    } catch (_error) {
      // Semantic text improves migration but must never block progress or bookmarks.
      return null
    }
  }

  captureToolbarSelection() {
    if (this.readOnlyValue) return
    if (this.pendingSelection) return
    this.captureSelection()
  }

  openMouseSelection(event) {
    if (this.readOnlyValue) return
    if (event.pointerType !== "mouse" || !this.captureSelection()) return

    this.openSelectionDialog()
  }

  openToolbarSelection() {
    if (this.readOnlyValue) return
    this.clearHighlightStatus()
    const hasSelectedText = Boolean(window.getSelection()?.toString().trim())
    if (!this.pendingSelection) this.captureSelection()
    if (!this.pendingSelection) {
      if (!hasSelectedText) this.showHighlightStatus()
      return
    }

    this.openSelectionDialog()
  }

  captureSelection() {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false

    const range = selection.getRangeAt(0)
    const startPage = range.startContainer.parentElement?.closest(".page")
    const endPage = range.endContainer.parentElement?.closest(".page")
    if (!startPage || startPage !== endPage) {
      this.statusTarget.textContent = this.labelsValue.crossPageSelection
      return false
    }

    const selectedText = selection.toString().trim()
    if (!selectedText) return false
    const page = Number(startPage.dataset.pageNumber)
    const rects = this.normalizedRects(range, startPage)
    const semanticLocator = this.semanticLocatorForRange(page, startPage, range, selectedText)
    if (rects.length === 0 || !semanticLocator) return false

    this.pendingSelection = { location: { format: "pdf", page, rects }, selectedText, semanticLocator }
    return true
  }

  normalizedRects(range, pageElement) {
    const pageRect = pageElement.getBoundingClientRect()
    return Array.from(range.getClientRects()).filter((rect) => rect.width > 0 && rect.height > 0).map((rect) => ({
      x: this.round((rect.left - pageRect.left) / pageRect.width),
      y: this.round((rect.top - pageRect.top) / pageRect.height),
      width: this.round(rect.width / pageRect.width),
      height: this.round(rect.height / pageRect.height)
    })).filter((rect) => rect.x >= 0 && rect.y >= 0 && rect.x + rect.width <= 1.01 && rect.y + rect.height <= 1.01)
  }

  semanticLocatorForRange(page, pageElement, range, selectedText) {
    const textLayer = pageElement.querySelector(".textLayer")
    if (!textLayer || !textLayer.contains(range.startContainer) || !textLayer.contains(range.endContainer)) return null

    const before = document.createRange()
    before.selectNodeContents(textLayer)
    before.setEnd(range.startContainer, range.startOffset)
    const startOffset = normalizeSearchText(before.toString()).length
    const normalizedLayer = normalizeSearchNode(textLayer).text
    const length = normalizeSearchText(selectedText).length
    return {
      format: "pdf",
      source: { page },
      block_index: 0,
      start_offset: startOffset,
      end_offset: startOffset + length,
      quote: selectedText.slice(0, 200),
      prefix: normalizedLayer.slice(Math.max(0, startOffset - 80), startOffset),
      suffix: normalizedLayer.slice(startOffset + length, startOffset + length + 80)
    }
  }

  openSelectionDialog() {
    if (!this.pendingSelection || this.selectionDialogTarget.open) return

    this.pendingHighlightColor = readHighlightColor(this.highlightColorsValue)
    selectColorButtons(this.selectionColorTargets, this.pendingHighlightColor)
    this.selectionQuoteTarget.textContent = this.pendingSelection.selectedText
    window.getSelection()?.removeAllRanges()
    this.selectionDialogTarget.showModal()
  }

  dismissSelection(event) {
    if (this.selectionClosePreservesPendingSelection(event)) return

    event?.preventDefault()
    if (this.selectionDialogTarget.open) this.selectionDialogTarget.close()
    window.getSelection()?.removeAllRanges()
    this.selectionQuoteTarget.textContent = ""
    this.pendingSelection = null
  }

  async persistSelection(addNote) {
    if (this.readOnlyValue) return
    if (this.offlineMode) return
    const selection = this.pendingSelection
    if (!selection) return

    await this.loadPanel("highlights")
    if (!this.loadedPanels.has("highlights")) return

    const key = this.highlightLocationKey(selection.location)
    const existing = this.highlightRecords.find((record) => this.highlightLocationKey(record.location) === key)
    if (existing) {
      this.dismissSelection()
      if (addNote) this.openNote(existing)
      return
    }
    if (this.pendingHighlightLocations.has(key)) return

    this.pendingHighlightLocations.add(key)
    try {
      const highlight = await requestJson(this.highlightsUrlValue, {
        method: "POST",
        body: {
          highlight: {
            color: validHighlightColor(this.highlightColorsValue, this.pendingHighlightColor) ? this.pendingHighlightColor : DEFAULT_HIGHLIGHT_COLOR,
            location: selection.location,
            selected_text: selection.selectedText,
            semantic_locator: selection.semanticLocator
          }
        }
      })
      if (!highlight) return

      this.highlightRecords.push(highlight)
      this.renderHighlightsForPage(highlight.location.page)
      this.renderHighlights()
      this.dismissSelection()
      if (addNote) this.openNote(highlight)
    } finally {
      this.pendingHighlightLocations.delete(key)
    }
  }

  renderHighlightsForPage(pageNumber) {
    const page = this.pageElement(pageNumber)
    if (!page) return

    page.querySelector(".pdf-saved-highlight-layer")?.remove()
    const layer = document.createElement("div")
    layer.className = "pdf-saved-highlight-layer"
    this.highlightRecords.filter((highlight) => highlight.location.page === pageNumber).forEach((highlight) => {
      const color = highlightColor(this.highlightColorsValue, highlight.color)
      const group = document.createElement("div")
      group.className = "pdf-saved-highlight-group"
      group.dataset.highlightId = highlight.id
      group.style.setProperty("--highlight-color", color.hex)
      highlight.location.rects.forEach((rect) => {
        const marker = document.createElement("button")
        marker.type = "button"
        marker.className = "pdf-saved-highlight"
        marker.style.cssText = `left:${rect.x * 100}%;top:${rect.y * 100}%;width:${rect.width * 100}%;height:${rect.height * 100}%`
        marker.addEventListener("click", () => this.openNote(highlight))
        group.append(marker)
      })
      layer.append(group)
    })
    page.append(layer)
  }

  async loadOutline() {
    const outline = await this.pdfDocument.getOutline()
    if (!outline?.length) {
      this.tocTarget.innerHTML = `<p class="reader-empty">${this.escape(this.labelsValue.noOutline)}</p>`
      return
    }

    this.tocTarget.replaceChildren(...this.outlineControls(outline))
  }

  outlineControls(items, depth = 0) {
    return items.flatMap((item) => [
      this.outlineControl(item, depth),
      ...this.outlineControls(item.items || [], depth + 1)
    ])
  }

  outlineControl(item, depth) {
    const externalUrl = this.safeExternalUrl(item.url)
    if (externalUrl) {
      const link = document.createElement("a")
      link.className = "reader-list-button"
      link.textContent = item.title.trim()
      link.href = externalUrl
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.style.paddingInlineStart = `${0.75 + Math.min(depth, 6) * 0.75}rem`
      return link
    }

    const button = document.createElement("button")
    button.type = "button"
    button.className = "reader-list-button"
    button.textContent = item.title.trim()
    button.disabled = !item.dest
    button.style.paddingInlineStart = `${0.75 + Math.min(depth, 6) * 0.75}rem`
    button.addEventListener("click", async () => {
      const destination = typeof item.dest === "string" ? await this.pdfDocument.getDestination(item.dest) : item.dest
      if (!destination) return

      const pageIndex = await this.pdfDocument.getPageIndex(destination[0])
      await this.scrollToPosition(pageIndex + 1, 0)
      this.closeSidebarAfterNavigation()
    })
    return button
  }

  safeExternalUrl(value) {
    if (!value) return null

    try {
      const url = new URL(value)
      return ["http:", "https:"].includes(url.protocol) ? url.toString() : null
    } catch (_error) {
      return null
    }
  }

  async searchBook(event) {
    event.preventDefault()
    if (this.offlineMode) return
    await this.readerSearch.load(this.searchInputTarget.value)
  }

  async openInitialSearch() {
    const search = this.initialSearchValue
    if (!search?.query) return

    this.activatePanel("search")
    this.setSidebarOpen(true)
    this.searchInputTarget.value = search.query
    await this.readerSearch.load(search.query, search)
  }

  resetSearchHighlights() {
    this.removeSearchResultHighlights()
    this.currentSearchOverlay?.remove()
    this.currentSearchOverlay = null
    this.currentSearchResult = null
    this.currentSearchResultKey = null
  }

  async openSearchResult(result, { closeSidebar = true } = {}) {
    try {
      await this.scrollToPosition(result.source.page, 0)
      const initialMatch = await this.waitForSearchRange(result)
      if (!initialMatch) throw new Error("PDF.js text extraction does not match the indexed text")

      initialMatch.range.startContainer.parentElement?.scrollIntoView({ block: "center" })
      if (!await this.waitForSearchPageToSettle(result)) throw new Error("PDF page did not settle after navigation")

      const match = await this.waitForSearchOverlay(result)
      if (!match) throw new Error("PDF.js text extraction does not match the indexed text")

      this.currentSearchOverlay?.remove()
      this.currentSearchResult = result
      this.currentSearchResultKey = this.searchResultKey(result)
      this.currentSearchOverlay = match.overlay
      if (closeSidebar) this.closeSidebarAfterNavigation()
      if (this.sidebarTarget.classList.contains("is-open")) this.renderSearchResultHighlightsForPage(result.source.page)
    } catch (error) {
      console.error("Unable to open PDF search result", error)
      this.searchStatusTarget.textContent = this.labelsValue.searchNavigationError
    }
  }

  async waitForSearchRange(result) {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      const match = this.searchRangeForResult(result)
      if (match) return match
      await new Promise((resolve) => window.setTimeout(resolve, SEARCH_RETRY_INTERVAL))
    }
    return null
  }

  async waitForSearchPageToSettle(result) {
    let previousPage = null
    let previousRect = null
    let stableDuration = 0

    for (let attempt = 0; attempt < 40; attempt += 1) {
      const match = this.searchRangeForResult(result)
      const rect = match?.page.getBoundingClientRect()
      const samePage = match?.page === previousPage
      const sameLayout = rect && previousRect && ["top", "left", "width", "height"].every((key) =>
        Math.abs(rect[key] - previousRect[key]) < 0.5
      )

      stableDuration = samePage && sameLayout ? stableDuration + SEARCH_RETRY_INTERVAL : 0
      if (stableDuration >= SEARCH_SETTLE_DURATION) return match

      previousPage = match?.page || null
      previousRect = rect
      await new Promise((resolve) => window.setTimeout(resolve, SEARCH_RETRY_INTERVAL))
    }
    return null
  }

  async waitForSearchOverlay(result) {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      const match = this.searchRangeForResult(result)
      const overlay = match && this.appendSearchOverlay(match.page, match.range, "pdf-search-highlight-layer")
      await new Promise((resolve) => window.setTimeout(resolve, SEARCH_RETRY_INTERVAL))
      if (overlay?.isConnected && match.page === this.pageElement(result.source.page)) return {...match, overlay}
      overlay?.remove()
    }
    return null
  }

  searchRangeForResult(result) {
    const page = this.pageElement(result.source.page)
    const textLayer = page?.querySelector(".textLayer")
    if (!page || !textLayer?.childElementCount) return null

    const range = rangeForPdfTextAnchor(textLayer, {
      preferredOffset: result.match_offset,
      quote: this.searchInputTarget.value,
      prefix: result.prefix,
      suffix: result.suffix
    })
    return range ? {page, range} : null
  }

  renderSearchResultHighlights() {
    this.removeSearchResultHighlights()
    if (!this.searchResultsVisible()) return

    new Set(this.readerSearch.records.map((result) => result.source.page)).forEach((pageNumber) => {
      this.renderSearchResultHighlightsForPage(pageNumber)
    })
  }

  renderCurrentSearchHighlightForPage(pageNumber) {
    const result = this.currentSearchResult
    if (!result || result.source.page !== pageNumber) return

    const page = this.pageElement(pageNumber)
    const textLayer = page?.querySelector(".textLayer")
    if (!page || !textLayer?.childElementCount) return

    const range = rangeForPdfTextAnchor(textLayer, {
      preferredOffset: result.match_offset,
      quote: this.searchInputTarget.value,
      prefix: result.prefix,
      suffix: result.suffix
    })
    if (!range) return

    const overlay = this.appendSearchOverlay(page, range, "pdf-search-highlight-layer")
    if (!overlay) return

    this.currentSearchOverlay?.remove()
    this.currentSearchOverlay = overlay
  }

  renderSearchResultHighlightsForPage(pageNumber) {
    const page = this.pageElement(pageNumber)
    page?.querySelector(".pdf-search-results-layer")?.remove()
    if (!page || !this.searchResultsVisible()) return

    const textLayer = page.querySelector(".textLayer")
    if (!textLayer?.childElementCount) return

    const overlay = document.createElement("div")
    overlay.className = "pdf-search-results-layer"
    overlay.style.setProperty("--search-highlight-color", this.highlightColorsValue.blue.hex)
    this.readerSearch.records.filter((result) =>
      result.source.page === pageNumber && this.searchResultKey(result) !== this.currentSearchResultKey
    ).forEach((result) => {
      const range = rangeForPdfTextAnchor(textLayer, {
        preferredOffset: result.match_offset,
        quote: this.searchInputTarget.value,
        prefix: result.prefix,
        suffix: result.suffix
      })
      if (!range) return

      this.normalizedRects(range, page).forEach((rect) => {
        const marker = document.createElement("span")
        marker.style.cssText = `left:${rect.x * 100}%;top:${rect.y * 100}%;width:${rect.width * 100}%;height:${rect.height * 100}%`
        overlay.append(marker)
      })
    })
    if (overlay.childElementCount) page.append(overlay)
  }

  removeSearchResultHighlights() {
    this.viewerTarget.querySelectorAll(".pdf-search-results-layer").forEach((overlay) => overlay.remove())
  }

  searchResultsVisible() {
    return this.readerSidebar.searchVisible()
  }

  searchResultKey(result) {
    return `${result.fragment_id}:${result.match_offset}`
  }

  appendSearchOverlay(page, range, className) {
    const overlay = document.createElement("div")
    overlay.className = className
    overlay.style.setProperty("--search-highlight-color", this.highlightColorsValue.blue.hex)
    this.normalizedRects(range, page).forEach((rect) => {
      const marker = document.createElement("span")
      marker.style.cssText = `left:${rect.x * 100}%;top:${rect.y * 100}%;width:${rect.width * 100}%;height:${rect.height * 100}%`
      overlay.append(marker)
    })
    if (!overlay.childElementCount) return null

    page.append(overlay)
    return overlay
  }

  async loadHighlights() {
    if (this.readOnlyValue) return false
    if (this.offlineMode) return false

    const records = await requestJson(this.highlightsUrlValue)
    if (!records) return false

    this.highlightRecords = records
    this.renderHighlights()
    this.highlightRecords.forEach((highlight) => this.renderHighlightsForPage(highlight.location.page))
    return true
  }

  async loadBookmarks() {
    if (this.readOnlyValue) return false
    if (this.offlineMode) return false

    const records = await requestJson(this.bookmarksUrlValue)
    if (!records) return false

    this.bookmarkRecords = records
    this.bookmarksLoaded = true
    this.bookmarkButtonTarget.disabled = false
    this.renderBookmarks()
    this.syncBookmarkButton()
    return true
  }

  visibleBookmarks(position = this.currentPosition()) {
    return this.bookmarkRecords.filter((bookmark) => Number(bookmark.location.page) === position.page)
  }

  syncBookmarkButton(position = this.currentPosition()) {
    const active = this.visibleBookmarks(position).length > 0
    this.bookmarkButtonTarget.classList.toggle("is-active", active)
    this.bookmarkButtonTarget.setAttribute("aria-pressed", String(active))
  }

  renderHighlights() {
    renderHighlightList({
      target: this.highlightsTarget,
      records: this.highlightRecords,
      filterColor: this.highlightFilterColor,
      palette: this.highlightColorsValue,
      labels: this.labelsValue,
      onNavigate: async (highlight) => {
        await this.scrollToPosition(highlight.location.page, highlight.location.rects[0]?.y || 0)
        this.closeSidebarAfterNavigation()
      },
      onEdit: (highlight) => this.openNote(highlight),
      onShare: (highlight) => this.shareAnnotation(highlight),
      onRemove: (highlight) => this.removeHighlight(highlight)
    })
  }

  renderBookmarks() {
    renderBookmarkList({
      target: this.bookmarksTarget,
      records: this.bookmarkRecords,
      labels: this.labelsValue,
      labelFor: (bookmark) => this.labelsValue.pageNumber.replace("%{page}", bookmark.location.page),
      onNavigate: async (bookmark) => {
        await this.scrollToPosition(bookmark.location.page, bookmark.location.y)
        this.closeSidebarAfterNavigation()
      },
      onShare: (bookmark) => this.shareAnnotation(bookmark),
      onRemove: (bookmark) => this.removeBookmark(bookmark)
    })
  }

  shareAnnotation(record) {
    if (this.readOnlyValue) return
    if (this.offlineMode) return
    return shareAnnotationLocation({ record, request: requestJson, clipboard: navigator.clipboard })
  }

  openNote(highlight) {
    if (this.readOnlyValue) return
    if (this.offlineMode) return

    this.pendingHighlight = highlight
    openNoteDialog({
      highlight,
      dialog: this.noteDialogTarget,
      title: this.noteTitleTarget,
      input: this.noteInputTarget,
      status: this.colorStatusTarget,
      colorButtons: this.noteColorTargets
    })
  }

  async changeHighlightColor(event) {
    if (this.readOnlyValue) return
    if (this.offlineMode) return

    const highlight = this.pendingHighlight
    const color = event.currentTarget.dataset.highlightColor
    await updateHighlightColor({
      highlight,
      color,
      palette: this.highlightColorsValue,
      buttons: this.noteColorTargets,
      status: this.colorStatusTarget,
      errorLabel: this.labelsValue.colorUpdateError,
      request: requestJson,
      onUpdated: (updatedHighlight) => {
        this.renderHighlightsForPage(updatedHighlight.location.page)
        this.renderHighlights()
      }
    })
  }

  async saveNote(event) {
    event.preventDefault()
    if (this.readOnlyValue) return
    if (this.offlineMode) return

    const highlight = this.pendingHighlight
    const body = this.noteInputTarget.value.trim()
    if (!highlight) return

    if (await saveHighlightNote({ highlight, body, request: requestJson })) {
      this.renderHighlights()
      this.closeNote()
    }
  }

  closeNote() {
    closeNoteDialog({ dialog: this.noteDialogTarget, input: this.noteInputTarget })
    this.pendingHighlight = null
  }

  async removePendingHighlight() {
    if (this.readOnlyValue) return
    if (await this.removeHighlight(this.pendingHighlight)) this.closeNote()
  }

  async removeHighlight(highlight) {
    if (this.readOnlyValue) return false
    if (this.offlineMode) return false

    const records = await removeHighlightRecord({ highlight, records: this.highlightRecords, request: requestJson })
    if (!records) return false
    this.highlightRecords = records
    this.renderHighlightsForPage(highlight.location.page)
    this.renderHighlights()
    return true
  }

  async removeBookmark(bookmark) {
    if (this.offlineMode) return

    await this.removeBookmarks([bookmark])
  }

  async removeBookmarks(bookmarks) {
    if (this.readOnlyValue) return
    if (this.offlineMode) return

    this.bookmarkRecords = await removeBookmarkRecords({ bookmarks, records: this.bookmarkRecords, request: requestJson })
    this.renderBookmarks()
    this.syncBookmarkButton()
  }

  safeLink(event) {
    const link = event.target.closest("a[href]")
    if (!link) {
      if (event.target.closest(".annotationLayer")) {
        event.preventDefault()
        event.stopImmediatePropagation()
      }
      return
    }

    const raw = link.getAttribute("href")
    if (raw?.startsWith("#")) return
    try {
      const url = new URL(raw, window.location.href)
      if (["http:", "https:"].includes(url.protocol)) {
        link.target = "_blank"
        link.rel = "noopener noreferrer"
        return
      }
    } catch (_error) {
      // Invalid and unsupported destinations are blocked below.
    }
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  handleKeyboardShortcut(event) {
    if ((event.metaKey || event.ctrlKey) && ["p", "s"].includes(event.key.toLowerCase())) {
      event.preventDefault()
      return
    }

    if (readerFindShortcut(event)) {
      event.preventDefault()
      this.openSearchPanel()
      return
    }

    if (!readerPageShortcut(event)) return
    event.preventDefault()
    event.key === "ArrowLeft" ? this.previous() : this.next()
  }

  async savePreferences() {
    if (this.readOnlyValue) return
    if (await this.offlineSession.savePreferences({ zoom: this.zoomValue })) return

    await requestJson(this.preferencesUrlValue, {
      method: "PATCH",
      body: { user: { reader_theme: this.themeValue, reader_pdf_zoom: this.zoomValue } }
    })
  }

  pageElement(pageNumber) {
    return this.viewerTarget.querySelector(`.page[data-page-number="${pageNumber}"]`)
  }

  round(value) {
    return Math.round(value * 100_000) / 100_000
  }

  highlightLocationKey(location) {
    const rects = location.rects.map(({ x, y, width, height }) => [x, y, width, height].map(Number))
    return JSON.stringify([location.format, Number(location.page), rects])
  }

  nextFrame() {
    return new Promise((resolve) => window.requestAnimationFrame(() => resolve()))
  }

  reconnect() {
    return this.offlineSession?.reconnect()
  }

  disconnected() {
    this.offlineSession?.disconnected()
  }

  renderOfflineStorageUnavailable() {
    this.application.getControllerForElementAndIdentifier(this.element, "offline-pdf")?.render("unsupported")
  }

  invalidateOfflineReader() {
    window.clearTimeout(this.progressTimer)
    window.location.reload()
  }

  closePdfDocument() {
    this.abortController?.abort()
    this.loadingTask?.destroy()
    this.pdfDocument = null
    this.viewerTarget.replaceChildren()
  }

  setOfflineMode(enabled) {
    this.offlineMode = enabled
    this.element.dataset.pdfReaderAccessMode = enabled ? "offline" : "online"
    if (enabled) {
      this.closeNote()
    }
    this.element.classList.toggle("is-offline", enabled)
    this.setTranslationOffline(enabled)
    this.offlineRestrictedTargets.forEach((element) => {
      element.disabled = enabled || (element === this.bookmarkButtonTarget && !this.bookmarksLoaded)
      element.setAttribute("aria-disabled", String(enabled))
    })
    if (this.hasOfflineIndicatorTarget) {
      this.offlineIndicatorTarget.hidden = !enabled
      this.offlineIndicatorTarget.textContent = enabled ? this.labelsValue.offline : ""
    }
    if (enabled && this.readerSidebar) this.readerSidebar.activate("toc")
    this.dispatch("access-mode", { detail: { offline: enabled } })
  }

}
